import config from "#src/config";
import { useCustomAppRegistry } from "#src/contexts/CustomAppRegistryContext";
import {
  getCustomAppEntries,
  type CustomAppEntryType,
} from "#src/view-models/custom-apps";
import {
  Icon,
  type SideNavigationTabType,
} from "@validereinc/common-components";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import moduleStyles from "./CustomAppSideNavigationTabs.module.scss";

const cx = classNames.bind(moduleStyles);

const EXTERNAL_LINK_ICON_SIZE_IN_PIXELS = 24;

/**
 * Map custom app entries to side navigation tabs that open the app in a new
 * tab. The description is the link's native tooltip, which keeps working when
 * the side navigation is collapsed to icons. When expanded, an external link
 * icon on the right signals that the app opens elsewhere, the way an
 * expandable tab's caret does. The side navigation only passes an icon to
 * render when collapsed, where there's no room for it.
 */
export const getCustomAppSideNavTabs = (
  entries: CustomAppEntryType[]
): SideNavigationTabType[] =>
  entries.map(({ name, title, icon, description, url }) => ({
    id: `custom_app_${name}`,
    title,
    icon,
    renderLink: (linkData) => (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={description}
        aria-label={title}
        className={linkData.className}
      >
        {linkData.icon ?? (
          <>
            {linkData.title}
            <Icon
              variant="arrow-square-out"
              size={EXTERNAL_LINK_ICON_SIZE_IN_PIXELS}
              className={cx("custom-app-link__external-icon")}
            />
          </>
        )}
      </a>
    ),
  }));

/** The side navigation tabs for the custom apps the current user can access, for the environment the web app is running in. */
export const useCustomAppSideNavTabs = (): SideNavigationTabType[] => {
  const { accessibleCustomAppNames } = useCustomAppRegistry();

  return useMemo(
    () =>
      getCustomAppSideNavTabs(
        getCustomAppEntries({
          registeredAppNames: accessibleCustomAppNames,
          environment: config.ENV,
        })
      ),
    [accessibleCustomAppNames]
  );
};
